import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import type { NativeTaskDTO } from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { trpc } from '@/lib/trpc'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { NewRequestDialog } from '@/components/employee-center/NewRequestDialog'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/employee-center/requests',
)({
  component: RouteComponent,
})

type BadgeStatus = 'default' | 'green' | 'amber' | 'blue' | 'purple'

// Employee-facing view of a request's lifecycle (pending → in progress → done),
// derived from the underlying TaskStatus.
const STATUS_META: Record<
  NativeTaskDTO['status'],
  { label: string; status: BadgeStatus }
> = {
  OPEN: { label: 'Pending', status: 'default' },
  ASSIGNED: { label: 'In progress', status: 'blue' },
  IN_PROGRESS: { label: 'In progress', status: 'blue' },
  IN_REVIEW: { label: 'In review', status: 'purple' },
  BLOCKED: { label: 'Blocked', status: 'amber' },
  RESOLVED: { label: 'Done', status: 'green' },
  DISMISSED: { label: 'Closed', status: 'default' },
}

function RouteComponent() {
  const navigate = useNavigate()
  const { workspaceId } = Route.useParams()
  const [dialogOpen, setDialogOpen] = useState(false)
  const requestsQuery = useQuery(
    trpc.task.myRequests.queryOptions({ limit: 50 }),
  )
  const requests = requestsQuery.data?.items ?? []

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-end'>
        <Button
          size='sm'
          className='h-8 rounded-sm text-[12px]'
          onClick={() => setDialogOpen(true)}
        >
          <Plus className='h-3.5 w-3.5' />
          New request
        </Button>
      </div>

      {requestsQuery.isPending ? (
        <div className='space-y-2'>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className='h-14 w-full rounded-sm' />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <div className='rounded-md border border-border bg-card px-6 py-12 text-center'>
          <div className='font-heading text-[14px] font-semibold text-text-secondary'>
            No requests yet
          </div>
          <p className='font-body mt-2 text-[12px] text-text-subtle'>
            File a request (access to a tool, a new device, or anything else)
            and track its status here.
          </p>
          <Button
            size='sm'
            className='mt-4 h-8 rounded-sm text-[12px]'
            onClick={() => setDialogOpen(true)}
          >
            <Plus className='h-3.5 w-3.5' />
            New request
          </Button>
        </div>
      ) : (
        <div className='overflow-hidden rounded-md border border-border bg-card'>
          <table className='w-full text-[13px]'>
            <thead>
              <tr className='border-b border-border bg-surface-subtle'>
                <th className='px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[1px] text-text-subtle'>
                  Request
                </th>
                <th className='w-[180px] px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[1px] text-text-subtle'>
                  Assigned To
                </th>
                <th className='w-[130px] px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[1px] text-text-subtle'>
                  Status
                </th>
                <th className='w-[130px] px-4 py-3 text-right text-[11px] font-medium uppercase tracking-[1px] text-text-subtle'>
                  Submitted
                </th>
                <th className='w-[130px] px-4 py-3 text-right text-[11px] font-medium uppercase tracking-[1px] text-text-subtle'>
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => {
                const meta = STATUS_META[request.status]
                return (
                  <tr
                    key={request.id}
                    onClick={() =>
                      navigate({
                        to: '/workspaces/$workspaceId/employee-center/requests/$taskId',
                        params: { workspaceId, taskId: request.id },
                      })
                    }
                    className='cursor-pointer border-b border-border last:border-b-0 hover:bg-surface-subtle'
                  >
                    <td className='px-4 py-3'>
                      <div className='font-medium text-text-secondary'>
                        {request.title}
                      </div>
                      {request.description && (
                        <div className='mt-0.5 line-clamp-1 text-[12px] text-text-subtle'>
                          {request.description}
                        </div>
                      )}
                    </td>
                    <td className='px-4 py-3'>
                      {request.assignedTo ? (
                        <div className='flex items-center gap-2'>
                          <MemberAvatar
                            size='xs'
                            person={{
                              name:
                                request.assignedTo.name ??
                                request.assignedTo.email ??
                                'Assigned',
                              image: request.assignedTo.image,
                              isServiceAccount:
                                request.assignedTo.isServiceAccount,
                            }}
                          />
                          <span className='truncate text-[12px] text-text-secondary'>
                            {request.assignedTo.name ??
                              request.assignedTo.email ??
                              'Assigned'}
                          </span>
                        </div>
                      ) : (
                        <span className='text-[12px] text-text-placeholder'>
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td className='px-4 py-3'>
                      <Badge type='dot' status={meta.status}>
                        {meta.label}
                      </Badge>
                    </td>
                    <td className='px-4 py-3 text-right text-[12px] text-text-subtle'>
                      {new Date(request.createdAt).toLocaleDateString()}
                    </td>
                    <td className='px-4 py-3 text-right text-[12px] text-text-subtle'>
                      {new Date(request.updatedAt).toLocaleDateString()}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <NewRequestDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  )
}
